import { Formik, FormikHelpers, FormikProps } from "formik";
import { useCallback, useContext, useEffect, useState } from "react";
import { ObjectSchema, boolean, object } from "yup";
import { requiredClassifierId } from "../../../../../../../data/yupConstants";
import {
  CopyPortalLicenseClientEventsData,
  CopyPortalLicenseClientEventsResponse,
  ItemViewModel,
  ProgramReportInfo,
} from "../../../../../../../generated/data-contracts";
import {
  ErrorUtils,
  IAxiosError,
  LicenseesUrls,
  LicenseesUtils,
  api,
} from "../../../../../../../utils";
import { AvisLoader } from "../../../../../../shared/components";
import {
  AvisConfirmModalShort,
  AvisModal,
} from "../../../../../../shared/components/modal";
import { AvisContext } from "../../../../../../shared/context/AvisContext";
import { CopyProgramReportForm } from "./CopyProgramReportForm";
import { CopyProgramReportResultForm } from "./CopyProgramReportResultForm";

interface ICopyProgramReportProps {
  sourceProgramReport: ProgramReportInfo;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<IAxiosError | undefined>>;
}
export function CopyProgramReport(props: Readonly<ICopyProgramReportProps>) {
  const { sourceProgramReport, isModalOpen, setIsModalOpen, setError } = props;
  const { res } = useContext(AvisContext);
  const [isLoadingDestinations, setIsLoadingDestinations] =
    useState<boolean>(true);
  const [isSubmittingCopyRequest, setIsSubmittingCopyRequest] =
    useState<boolean>(false);
  const [copyDestinations, setCopyDestinations] = useState<ItemViewModel[]>([]);
  //Copy result modal state
  const [isCopySuccessModalOpen, setIsCopySuccessModalOpen] =
    useState<boolean>(false);
  const [copyResult, setCopyResult] =
    useState<CopyPortalLicenseClientEventsResponse>();
  useEffect(() => {
    if (!sourceProgramReport?.licenseClientEventId) return;

    setIsLoadingDestinations(true);
    api
      .licenseesGetProgramReportsForCopyDestination(
        sourceProgramReport.licenseClientEventId,
      )
      .then((res) => {
        const availableItems = res.data.map((item) => {
          return {
            id: item.licenseClientEventId,
            name: LicenseesUtils.formatProgramReportInfo(item),
          } as ItemViewModel;
        });
        setCopyDestinations(availableItems);
      })
      .catch(setError)
      .finally(() => setIsLoadingDestinations(false));
  }, [sourceProgramReport, setError]);

  const handleSubmit = async (
    values: CopyPortalLicenseClientEventsData,
    formikHelpers: FormikHelpers<CopyPortalLicenseClientEventsData>,
  ) => {
    if (!sourceProgramReport.licenseClientEventId) return;
    setIsSubmittingCopyRequest(true);

    api
      .licenseesCopyLicenseClientEventProgramReport(
        sourceProgramReport.licenseClientEventId,
        values,
      )
      .then((res) => {
        setCopyResult(res.data);
        setIsCopySuccessModalOpen(true);
      })
      .catch((err) => {
        ErrorUtils.handleFormValidation(err, formikHelpers);
      })
      .finally(() => setIsSubmittingCopyRequest(false));
  };

  const renderForm = useCallback(
    (formikProps: FormikProps<CopyPortalLicenseClientEventsData>) => {
      return (
        <>
          <AvisModal
            isOpen={isModalOpen}
            title={res("licensees_CopyProgramReport_Title")}
            confirmButtonText={res("button_Copy")}
            confirmButtonDisabled={
              !formikProps.values?.destinationPortalLicenseClientEventId
            }
            onConfirm={formikProps.submitForm}
            onClose={() => {
              formikProps.resetForm();
              setIsModalOpen(false);
            }}
          >
            {isLoadingDestinations && <AvisLoader inline />}
            {!isLoadingDestinations && (
              <CopyProgramReportForm
                bag={formikProps}
                availableDestinations={copyDestinations}
                sourceProgramReport={sourceProgramReport}
                loading={isSubmittingCopyRequest || isLoadingDestinations}
              />
            )}
          </AvisModal>
          <AvisConfirmModalShort
            className="program-report-copy-result-dialog"
            isOpen={isCopySuccessModalOpen || isSubmittingCopyRequest}
            buttons={!copyResult ? [] : undefined}
            content={<CopyProgramReportResultForm copyResult={copyResult} />}
            confirm={() => {
              location.replace(
                LicenseesUtils.getUrlWithParams(
                  LicenseesUrls.licenseeLicenseViewReportPP,
                  {
                    licenseId: copyResult?.licenseId,
                    licenseInvoicePlanId: copyResult?.licenseInvoicePlanId,
                    licenseClientEventId: copyResult?.licenseClientEventId,
                    licenseReportFormType:
                      LicenseesUtils.getUrlParams().licenseReportFormType,
                    isCopy: true,
                  },
                ),
              );
            }}
            cancel={() => {
              setCopyResult(undefined);
              formikProps.resetForm();
              setIsCopySuccessModalOpen(false);
            }}
            title={
              copyResult
                ? res(
                    "licensees_CopyProgramReport_ProgressDialog_Title_Completed",
                  )
                : res(
                    "licensees_CopyProgramReport_ProgressDialog_Title_InProgress",
                  )
            }
          />
        </>
      );
    },
    [
      sourceProgramReport,
      isModalOpen,
      copyDestinations,
      isLoadingDestinations,
      isSubmittingCopyRequest,
      isCopySuccessModalOpen,
      copyResult,
      setIsModalOpen,
      res,
    ],
  );

  const validationSchema: ObjectSchema<CopyPortalLicenseClientEventsData> =
    object({
      destinationPortalLicenseClientEventId: requiredClassifierId(res),
      overwriteExistingProgramReport: boolean(),
    });
  return (
    <Formik<CopyPortalLicenseClientEventsData>
      initialValues={{} as CopyPortalLicenseClientEventsData}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={validationSchema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {renderForm}
    </Formik>
  );
}
